import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
// 2026-05-16: ?diag=1 で診断パネル起動。Mac 無しで iOS Safari PWA の遅延 checkpoint を可視化する。
import { diagBoot } from './utils/diagnostic';
// 2026-05-17: iOS Safari PWA mode の index.html cache 問題対応。
// boot 時に /version.json を fetch、bundle 側 BUILD_VERSION と不一致なら自動 reload。
import { checkBuildVersion } from './utils/buildVersionCheck';
// 2026-08-24: 壊れた永続状態(IndexedDB)での login 全滅ロックアウトの自己修復(delete-before-init)。
//   ★このモジュールは firebase を import しない=firebase/config より前に安全に実行できる。
import { runPendingRecovery } from './utils/recoverFromCorruptedState';
// 2026-09-24 P2-2: login 変種の非ブロッキング解決(host allowlist)。★Firebase 非依存=main を汚さない(#129 隔離不変)。
import { kickAuthVariantResolution } from './individual/authVariantState';
// 2026-09-27 Codex MAJOR-1 是正: config.individual の variant1 並列 prewarm(boot と SPA 内遷移で共通 helper に一本化)。
import { prewarmIndividualConfig } from './individual/individualConfigPrewarm';
// 2026-09-25 P2 chunk gating(敵対レビュー MAJOR-1 是正): PWA ホームアイコンは start_url='/' で boot し
//   LauncherPage が SPA navigate(reload なし)で個人面へ着地する=個人客の日常再入場主経路。従来の path regex だけ
//   では '/' boot で warmup/kick/prewarm が全部不発になるため、Launcher と同じ純関数(decideDispatch)+同じ入力で
//   「個人面へ dispatch される boot か」を鏡像判定する。両 module とも Firebase-free(#129 隔離不変)。
import { decideDispatch } from './pages/launcherDispatch';
import { getIndividualLink, getLastCustomerCode, getLastTenantId, getLastMode } from './utils/customerCodeStorage';

diagBoot();

// 2026-08-24: delete-before-init 復旧 boot gate。
//   ★App を「静的 import」から「動的 import」に変更した理由: 静的 import だと App が読み込む
//     firebase/config(=initializeApp が IndexedDB を開く)が index.tsx 本体より先に評価されてしまう。
//     復旧の削除は「Firebase が箱を開く前」に行わないと deleteDatabase が blocked される(portal の敗因)。
//     そこで runPendingRecovery() を await した"後"で App を動的 import する=firebase/config は
//     削除完了後に初めて走る=箱を開く前に確実に消せる。
//   ★happy path: 復旧フラグが無ければ runPendingRecovery() は即 return し、削除も何もしない
//     (挙動は従来と同一・auth core 非接触)。App の動的 import は追加の chunk 取得1回のみ(以後 cache)。
async function boot() {
  try {
    await runPendingRecovery();
  } catch (e) {
    // 復旧が失敗しても起動は続ける(無限ロックアウトより「多少壊れたまま起動」の方がマシ)。
    // eslint-disable-next-line no-console
    console.warn('[boot] runPendingRecovery failed (continuing):', e);
  }

  // 非同期、boot をブロックしない。React render 後、version 確認して必要なら reload。
  checkBuildVersion();

  // 2026-09-22 P2-1 latency(A案): 個人 login route の時だけ、個人 login ページ chunk を
  //   App import と並列に warm(fire-and-forget)= React.lazy の cold 初回 chunk fetch を
  //   boot と並列化し、code-split で増えた nav->form の往復を消す(preview 実測 約+480ms の是正)。
  //   ★route 条件つき=/i/ と /c/{tenant}/{customer}[/short] のみ。法人短縮 /c/{code}(単一) や
  //     corporate/admin には一切触れない(#129 隔離を保つ=無条件 preload は禁)。
  //   ★runPendingRecovery() の後に発火=delete-before-init 不変を壊さない。
  //     import() は fire-and-forget で await しない(App load を1msも遅らせない)・失敗は握り潰す(best-effort)。
  //   ★React.lazy(App.tsx)と同一 module specifier=webpack が同一 chunk へ dedupe=warm した promise を lazy が再利用。
  const bootPath = window.location.pathname;
  // '/' boot(PWA ホームアイコン)で Launcher が個人面(/i/... or /c/{t}/{c}[/{s}])へ navigate する場合も
  //   個人 lane として warm する(敵対レビュー MAJOR-1: この主経路で prewarm 不発だと variant1 の
  //   token_lookup に chunk 1 RTT が直列追加=login 不可侵違反)。判定は Launcher と同一の純関数+同一入力
  //   (=挙動の二重実装をせん)。corporate へ dispatch される boot では従来どおり一切発火しない(#129 隔離)。
  const launcherGoesIndividual = ((): boolean => {
    if (bootPath !== '/') return false;
    try {
      const d = decideDispatch(
        new URLSearchParams(window.location.search).get('force') === 'corporate',
        getIndividualLink(),
        getLastCustomerCode(),
        getLastTenantId(),
        getLastMode(),
      );
      return d.type === 'navigate' && (/^\/i\//.test(d.url) || /^\/c\/[^/]+\/[^/]+/.test(d.url));
    } catch {
      return false; // 判定失敗は従来挙動(warm なし)= fail-safe
    }
  })();
  if (/^\/i\//.test(bootPath) || /^\/c\/[^/]+\/[^/]+/.test(bootPath) || launcherGoesIndividual) {
    import('./individual/IndividualLoginPage').catch(() => {});
    import('./individual/IndividualShortCodeEntryPage').catch(() => {});
    // 2026-09-24 P2-2/P2-3: 個人 route の時だけ、login 変種を非ブロッキングで先行解決(fire-and-forget)。
    //   これで useMemberAuth の同期 read(getResolvedAuthVariant=consumed lock)が mount 時に大抵 variant2 解決済みを consume できる(先行 warm)。
    //   fail-safe=variant1・runPendingRecovery 後ゆえ delete-before-init 不変・corporate/admin は route 条件で不発。
    kickAuthVariantResolution();
    // 2026-09-25 P2 chunk gating / 2026-09-27 Codex MAJOR-1 是正: config.individual(secondaryApp=#129根因)を
    //   login chunk から析出したため、variant1 の従来 latency(chunk 評価時に構築開始)を守る並列 prewarm を発火する。
    //   判定は共通 helper に一本化(App.tsx の React.lazy factory も同じ helper を使う=SPA 内遷移でも同 tick prewarm)。
    //   本番=即 prewarm / 非本番=server 判定後 variant1 のみ / variant2=構築させない(helper 内で判定・behavior test で固定)。
    //   ★runPendingRecovery 後ゆえ delete-before-init 不変(従来の warmup と同じ位置)。
    prewarmIndividualConfig();
  }

  const { default: App } = await import('./App');
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}
// App の動的 import が失敗(chunk load 失敗等)しても unhandled rejection を出さない。
boot().catch((e) => {
  // eslint-disable-next-line no-console
  console.error('[boot] failed to start app:', e);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
